import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type { PermissionCatalog, Role, RolePayload } from '../types/roles';

/**
 * The backend leaves optional role fields null (most commonly `description` on
 * roles created without one). The UI treats them as plain strings — it calls
 * `.trim()` on them and spreads `permissionGroups` — so normalise once here at
 * the boundary rather than sprinkling null guards through every consumer.
 */
const normalizeRole = (role: Role): Role => ({
	...role,
	name: role.name ?? '',
	description: role.description ?? '',
	type: role.type ?? 'custom',
	status: role.status ?? 'active',
	permissionGroups: role.permissionGroups ?? [],
	permissionCount: role.permissionCount ?? 0,
	systemRole: role.systemRole ?? false,
});

export const rolesApi = {
	list: async (): Promise<Role[]> => {
		const roles = await apiClient.get<Role[]>(API_ENDPOINTS.SETTINGS.ROLES);
		return (roles ?? []).map(normalizeRole);
	},

	catalog: async (): Promise<PermissionCatalog> =>
		apiClient.get<PermissionCatalog>(API_ENDPOINTS.SETTINGS.PERMISSIONS),

	create: async (payload: RolePayload): Promise<Role> =>
		normalizeRole(await apiClient.post<Role>(API_ENDPOINTS.SETTINGS.ROLES, payload)),

	update: async (id: number, payload: RolePayload): Promise<Role> =>
		normalizeRole(await apiClient.put<Role>(API_ENDPOINTS.SETTINGS.ROLE_DETAIL(id), payload)),

	delete: async (id: number): Promise<void> => {
		await apiClient.delete<void>(API_ENDPOINTS.SETTINGS.ROLE_DETAIL(id));
	},
};
